import { Box, useTheme } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import { useCachedItemImage } from "../hooks/useCachedItemImage";
import { buildDefaultItemImage } from "../util/posItemPlaceholder";
import { ItemImageRole } from "../util/itemImageCache";

type Props = {
	itemId: number;
	alt?: string;
	role?: ItemImageRole;
	lazy?: boolean;
	/** cover for photos, contain for placeholder SVG */
	sx?: SxProps<Theme>;
	imgSx?: SxProps<Theme>;
};

/**
 * Product image: default SVG first, then IDB cache, then server (hashed + stored).
 */
const CachedItemImage = ({
	itemId,
	alt = "",
	role = "main",
	lazy = true,
	sx,
	imgSx,
}: Props) => {
	const theme = useTheme();
	const { api } = useAuth();
	const isDark = theme.palette.mode === "dark";
	const placeholder = useMemo(
		() => buildDefaultItemImage(theme.palette.primary.main, isDark),
		[theme.palette.primary.main, isDark],
	);

	const { src, isPlaceholder, imgRef } = useCachedItemImage(itemId, {
		placeholder,
		role,
		lazy,
		api,
		enabled: itemId > 0,
	});

	return (
		<Box
			ref={imgRef as React.Ref<HTMLDivElement>}
			sx={{
				width: "100%",
				height: "100%",
				position: "relative",
				overflow: "hidden",
				...sx,
			}}
		>
			<Box
				component="img"
				src={src}
				alt={alt}
				loading={lazy ? "lazy" : "eager"}
				sx={{
					display: "block",
					width: "100%",
					height: "100%",
					objectFit: isPlaceholder ? "contain" : "cover",
					objectPosition: "center",
					p: isPlaceholder ? 0.5 : 0,
					boxSizing: "border-box",
					...imgSx,
				}}
			/>
		</Box>
	);
};

export default CachedItemImage;
